import { memo, useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { useSendCoachCueFeedbackMutation } from "services/coach-sessions";

import {
  type CoachCue,
  dismissCoachCue,
  selectCoachCues,
  setCoachCueFeedback
} from "../medical-scribe.slice";

type PaletteTone = "error" | "warning" | "info" | "success" | "secondary";

// Maps a coaching category to a semantic MUI palette tone (Suno's chip system, not the
// prototype's bespoke hexes). compliance is always the critical/error tone.
const CATEGORY_TONE: Record<string, PaletteTone> = {
  compliance: "error",
  objection: "warning",
  stall: "warning",
  discovery: "info",
  opportunity: "success",
  buying_signal: "success"
};

const CATEGORY_LABEL: Record<string, string> = {
  compliance: "Compliance",
  objection: "Objection",
  stall: "Stall",
  discovery: "Discovery",
  opportunity: "Opportunity",
  buying_signal: "Buying signal"
};

function toneOf(category: string): PaletteTone {
  return CATEGORY_TONE[category] ?? "secondary";
}

function labelOf(category: string): string {
  return CATEGORY_LABEL[category] ?? category;
}

function remainingFraction(cue: CoachCue, now: number): number {
  const elapsed = (now - cue.receivedAt) / 1000;
  return Math.max(0, 1 - elapsed / Math.max(1, cue.decaySeconds));
}

function relativeTime(receivedAt: number, now: number): string {
  const secs = Math.max(0, Math.round((now - receivedAt) / 1000));
  if (secs < 60) return `${secs}s ago`;
  return `${Math.floor(secs / 60)}m ago`;
}

/**
 * Live coaching surface shown in the recording widget while a consult is being recorded.
 * The most recent un-dismissed, un-expired cue renders as the active card (with a decay
 * ring that counts down `decaySeconds`, echoing the prototype); older cues collapse into
 * a signal log. Renders nothing when no cues have arrived, so it is inert unless the
 * backend live coach is active for this session.
 */
function CoachCuePanel() {
  const dispatch = useAppDispatch();
  const cues = useAppSelector(selectCoachCues);
  const [now, setNow] = useState(() => Date.now());
  const [logOpen, setLogOpen] = useState(false);
  const [sendCueFeedback] = useSendCoachCueFeedbackMutation();

  // A thumb vote IS the dismissal for the active cue (forced feedback — a
  // plain X carries no signal); log rows vote without dismissal semantics.
  // Persistence is best-effort: the local state updates optimistically and a
  // failed POST is silent, per the coaching error-handling philosophy.
  const voteOnCue = (cue: CoachCue, vote: "up" | "down", dismiss: boolean) => {
    dispatch(setCoachCueFeedback({ id: cue.id, vote }));
    if (dismiss) {
      dispatch(dismissCoachCue(cue.id));
    }
    if (cue.cueId) {
      sendCueFeedback({ cueId: cue.cueId, vote });
    }
  };

  // 1s tick drives the decay ring while a cue is still live (not dismissed, not past its
  // decaySeconds). S16 (PZ review): dismissed/expired cues are retained for the log rather
  // than removed from `cues` — the array only empties at end-of-recording — so gating the
  // interval on `cues.length !== 0` alone kept it ticking for the rest of the recording long
  // after the last cue had decayed. Expiry is time-derived (no Redux change fires when a cue
  // naturally decays), so the tick checks liveness on itself and self-clears instead of
  // relying on an external dependency to tell it to stop.
  useEffect(() => {
    if (cues.length === 0) return undefined;
    const timer = setInterval(() => {
      // Update first so the tick that crosses the decay threshold still renders the
      // just-expired state — checking liveness before this point (and skipping the update
      // when already past decay) would freeze the UI one tick short of ever showing decay.
      const tickNow = Date.now();
      setNow(tickNow);
      const stillLive = cues.some(
        (cue) => !cue.dismissed && remainingFraction(cue, tickNow) > 0
      );
      if (!stillLive) {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [cues]);

  const activeCue = useMemo(
    () => cues.find((cue) => !cue.dismissed && remainingFraction(cue, now) > 0),
    [cues, now]
  );

  const logCues = useMemo(
    () => cues.filter((cue) => cue.id !== activeCue?.id).slice(0, 8),
    [cues, activeCue]
  );

  if (cues.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      {activeCue && (
        <Box
          sx={(theme: Theme) => {
            const tone = theme.palette[toneOf(activeCue.category)];
            return {
              position: "relative",
              backgroundColor: alpha(tone.main, 0.08),
              border: `1px solid ${alpha(tone.main, 0.35)}`,
              borderLeft: `3px solid ${tone.main}`,
              borderRadius: 1.5,
              p: 1.25,
              overflow: "hidden"
            };
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress
                variant="determinate"
                value={remainingFraction(activeCue, now) * 100}
                size={22}
                thickness={5}
                sx={(theme: Theme) => ({
                  color: theme.palette[toneOf(activeCue.category)].main
                })}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center"
                }}
              >
                <TipsAndUpdatesOutlinedIcon
                  sx={(theme: Theme) => ({
                    fontSize: 12,
                    color: theme.palette[toneOf(activeCue.category)].main
                  })}
                />
              </Box>
            </Box>
            <Chip
              size="small"
              label={labelOf(activeCue.category)}
              sx={(theme: Theme) => {
                const tone = theme.palette[toneOf(activeCue.category)];
                return {
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: alpha(tone.main, 0.16),
                  color: tone.dark
                };
              }}
            />
            {activeCue.priority === "critical" && (
              <Chip
                size="small"
                label="Critical"
                color="error"
                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }}
              />
            )}
            <Box sx={{ flexGrow: 1 }} />
            {activeCue.cueId ? (
              <>
                <Tooltip title="Helpful" disableInteractive>
                  <IconButton
                    size="small"
                    aria-label="Helpful coaching cue"
                    onClick={() => voteOnCue(activeCue, "up", true)}
                    sx={{ p: 0.25 }}
                  >
                    <ThumbUpOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Not helpful" disableInteractive>
                  <IconButton
                    size="small"
                    aria-label="Not helpful coaching cue"
                    onClick={() => voteOnCue(activeCue, "down", true)}
                    sx={{ p: 0.25 }}
                  >
                    <ThumbDownOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              // No backend cue id — nothing to vote on, so a plain dismiss is
              // the only affordance (the cue must never be un-dismissable).
              <IconButton
                size="small"
                aria-label="Dismiss coaching cue"
                onClick={() => dispatch(dismissCoachCue(activeCue.id))}
                sx={{ p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Stack>

          <Typography
            variant="body2"
            sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.25 }}
          >
            {activeCue.headline}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", lineHeight: 1.45 }}
          >
            {activeCue.advice}
          </Typography>

          {activeCue.suggestedActions.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.75 }}
            >
              {activeCue.suggestedActions.map((action) => (
                <Chip
                  key={action}
                  size="small"
                  variant="outlined"
                  label={action}
                  sx={{ height: 22, fontSize: "0.68rem" }}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {logCues.length > 0 && (
        <Box sx={{ mt: activeCue ? 0.75 : 0 }}>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => setLogOpen((open) => !open)}
            sx={{ fontSize: "0.7rem", color: "text.secondary" }}
          >
            {logOpen ? "Hide" : "Show"} coaching log ({logCues.length})
          </Link>
          <Collapse in={logOpen}>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {logCues.map((cue) => (
                <Stack
                  key={cue.id}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ opacity: cue.dismissed ? 0.55 : 1 }}
                >
                  <Box
                    sx={(theme: Theme) => ({
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      flexShrink: 0,
                      backgroundColor: theme.palette[toneOf(cue.category)].main
                    })}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      flexGrow: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {cue.headline}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", flexShrink: 0 }}
                  >
                    {relativeTime(cue.receivedAt, now)}
                  </Typography>
                  {cue.feedback ? (
                    cue.feedback === "up" ? (
                      <ThumbUpIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                    ) : (
                      <ThumbDownIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                    )
                  ) : cue.cueId ? (
                    // Post-hoc voting for cues that decayed before the provider
                    // could react — pure feedback, no dismissal semantics.
                    <>
                      <IconButton
                        size="small"
                        aria-label={`Helpful: ${cue.headline}`}
                        onClick={() => voteOnCue(cue, "up", false)}
                        sx={{ p: 0.25 }}
                      >
                        <ThumbUpOutlinedIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Not helpful: ${cue.headline}`}
                        onClick={() => voteOnCue(cue, "down", false)}
                        sx={{ p: 0.25 }}
                      >
                        <ThumbDownOutlinedIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

// S16 (PZ review): this component takes no props and reads everything from Redux/local
// state, so React.memo means a parent re-render (the widget re-renders per transcript turn)
// never re-renders this subtree unless its own subscribed state actually changed.
export default memo(CoachCuePanel);
